import React, { useContext, useMemo, useState } from "react";
import { Box, Container } from "@mui/material";
import Grid from "@mui/material/Grid";
import { useParams } from "react-router-dom";
import JobMonitor from "../../components/jobs/JobMonitor";
import { SnackBarMessage } from "../../modals/SnackBarMessage";
import { useSnackbarPosition } from "../../shared/hooks/useSnackbarPosition";
import { GenerateReportPageSkeleton } from "../../components/skeletons/GenerateReportPageSkeleton";
import { BodycamContext } from "../../context/BodycamContext";
import { NarrativeTemplate } from "../../shared/interfaces/ReportGenReport";
import { useJobStore } from "../../store/jobStore";
import { useErrorSnackbar } from "../../shared/hooks/useErrorSnackbar";
import { useVideoData, useReportGeneration } from "./hooks";
import {
  VideoEvidenceSection,
  ReportConfigSection,
  GenerateReportError,
} from "./components";
import { usePageHeader } from "../../context/LayoutContext";
import BoltIcon from "@mui/icons-material/Bolt";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";

const GenerateReportPage: React.FC = () => {
  const { evidenceID } = useParams();
  const isMaxJobsReached = useJobStore((state) => state.isMaxJobsReached());
  const { customReports } = useContext(BodycamContext);
  const [customReportTypes] = customReports;
  const { showError, snackBarMessage, snackBarMode, handleCloseSnackbar } = useErrorSnackbar();
  const snackbarPosition = useSnackbarPosition();

  // default report type
  const generalReportType: NarrativeTemplate = useMemo(
    () => ({
      template: { name: "general", content: "", stationId: "", templateId: "default", arguments: {} },
      examples: [],
    }),
    []
  );
  const [reportType, setReportType] = useState<NarrativeTemplate>(generalReportType);
  const [selectedFields, setSelectedFields] = useState<string[]>([]);

  const {
    videoData,
    videoDataList,
    currentPage,
    viewedVideos,
    loading,
    viewedCount,
    totalVideos,
    generateDisabled,
    handlePageChange,
    handleNextVideo,
    handlePrevVideo,
  } = useVideoData(evidenceID, showError);

  const { isGenerating, handleSubmitClick } = useReportGeneration(
    evidenceID,
    reportType,
    selectedFields,
    videoDataList,
    isMaxJobsReached,
    showError
  );

  const handleReportTypeChange = (value: NarrativeTemplate) => {
    setReportType(value);
  };

  const handleClearSelectedFields = () => setSelectedFields([]);

  // Set page header in app bar
  usePageHeader({
    title: "Generate Report",
    subtitle: "Review video evidence and create incident reports",
    icon: <BoltIcon />,
    chip: { label: `${videoDataList.length} ${videoDataList.length === 1 ? 'Video' : 'Videos'}`, icon: <PlayArrowIcon /> },
  });

  if (loading) {
    return (
      <>
        <SnackBarMessage
          handleClose={handleCloseSnackbar}
          mode={snackBarMode}
          message={snackBarMessage}
          horizontalAnchor="center"
          verticalAnchor="top"
          sxOverrides={snackbarPosition}
        />
        <Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
          <GenerateReportPageSkeleton />
        </Box>
      </>
    );
  }

  if (!videoData) {
    return (
      <GenerateReportError
        snackBarMessage={snackBarMessage}
        snackBarMode={snackBarMode}
        handleCloseSnackbar={handleCloseSnackbar}
        onRetry={() => window.location.reload()}
      />
    );
  }

  return (
    <>
      <JobMonitor />
      <SnackBarMessage
        handleClose={handleCloseSnackbar}
        mode={snackBarMode}
        message={snackBarMessage}
        horizontalAnchor="center"
        verticalAnchor="top"
        sxOverrides={snackbarPosition}
      />
      <Container sx={{ minWidth: { xs: "200px", sm: "450px" }, overflow: "auto" }}>
        <Grid container spacing={3} direction="column" sx={{ display: "flex", alignItems: "flex-start", mb: 2 }}>
          <Grid size={{ xs: 12 }}>
            <Grid container spacing={2} sx={{ width: "100%" }}>
              <Grid size={{ xs: 12, md: 8 }}>
                <VideoEvidenceSection
                  videoData={videoData as NonNullable<typeof videoData>}
                  videoDataListLength={videoDataList.length}
                  currentPage={currentPage}
                  viewedVideos={viewedVideos}
                  handlePageChange={handlePageChange}
                  handlePrevVideo={handlePrevVideo}
                  handleNextVideo={handleNextVideo}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 4 }}>
                <ReportConfigSection
                  reportType={reportType}
                  generalReportType={generalReportType}
                  customReportTypes={customReportTypes ?? undefined}
                  onReportTypeChange={handleReportTypeChange}
                  selectedFields={selectedFields}
                  onClearSelectedFields={handleClearSelectedFields}
                  onGenerate={handleSubmitClick}
                  isGenerating={isGenerating}
                  isMaxJobsReached={isMaxJobsReached}
                  generateDisabled={generateDisabled}
                  selectedFieldsCount={selectedFields.length}
                  viewedCount={viewedCount}
                  totalVideos={totalVideos}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Container>
    </>
  );
};

export default GenerateReportPage;
export { GenerateReportPage };
